import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { AUTH_PATHS } from "../../../constants";
import { createPublicAPI } from "../../../services/apiService";
import { AuthService } from "../../../services/authService";
import { getErrorMessage, getPostAuthRedirect } from "../../../utils";
import { AuthLayout } from "../AuthLayout";
import { EmailConfirmationView } from "./EmailConfirmationView";
import { useEmailConfirmationPoll } from "./useEmailConfirmationPoll";

export const EmailConfirmation = () => {
  const isConfirmed = useEmailConfirmationPoll();
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromState = location.state?.email;

  // Separate from the polling hook above so this timeout's own lifecycle
  // (set once isConfirmed flips true; cleared on true unmount OR on any dep
  // change) isn't tangled with the polling effect's re-run when isConfirmed
  // changes. Both a true unmount and a StrictMode double-invoke clear-then-reset
  // this timer correctly with no refs needed.
  useEffect(() => {
    if (!isConfirmed) {
      return;
    }

    const redirectTimeout = setTimeout(() => {
      const { redirectPath } = getPostAuthRedirect({ location });
      navigate(redirectPath, { replace: true });
    }, 2000);

    return () => {
      clearTimeout(redirectTimeout);
    };
  }, [isConfirmed, navigate, location]);

  const redirectBackToLogin = async () => {
    navigate(AUTH_PATHS.SIGNIN, { replace: true });
  };

  const handleResendEmail = async () => {
    if (!emailFromState || isResending) {
      return;
    }

    setIsResending(true);
    try {
      const publicApi = createPublicAPI("v2");
      const authService = new AuthService(publicApi);
      const response = await authService.resendConfirmationEmail(emailFromState);
      toast.success(response.message);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsResending(false);
    }
  };

  if (!emailFromState) {
    return <Navigate to={AUTH_PATHS.SIGNIN} replace />;
  }

  return (
    <AuthLayout>
      <EmailConfirmationView
        isConfirmed={isConfirmed}
        email={emailFromState}
        redirectBackToLogin={redirectBackToLogin}
        onResendEmail={handleResendEmail}
        isResending={isResending}
      />
    </AuthLayout>
  );
};
